import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProjectEmbeddings, getLinkedEmbeddings, setSelectedProject } from '../../store/slices/projectSlice';
import { ApiPost } from '../../helpers/API/ApiData';
import toast from 'react-hot-toast';
import Button from '../button';
import './kbselection.scss';

import PdfIcon from '../../assets/icons/pdffile.svg';
import DocIcon from '../../assets/icons/docfile.svg';
import TextIcon from '../../assets/icons/textfile.svg';
import SimpleTextIcon from '../../assets/icons/simpletext.svg';

const fileIcons = {
    pdf_file: PdfIcon,
    doc_file: DocIcon,
    text_file: TextIcon,
    simple_text: SimpleTextIcon,
};

export default function KBSelection({ projectId, onSave }) {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector((state) => state.project);
    const [allEmbeddings, setAllEmbeddings] = useState([]);
    const [linkedIds, setLinkedIds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            // Fetch embeddings for the project owner (not current logged-in user)
            // This ensures admin sees the user's files when editing their chatbot
            const allRes = await ApiPost('embedding/list-by-project-owner', { project_id: projectId });
            if (allRes?.data?.success && allRes?.data?.data) {
                // API might return single object or array
                const embeddings = Array.isArray(allRes.data.data)
                    ? allRes.data.data
                    : [allRes.data.data];
                setAllEmbeddings(embeddings.filter(e => e)); // Filter out nulls
            }

            // Fetch linked embeddings for this project
            const linkedRes = await dispatch(getLinkedEmbeddings({ id: projectId }));
            if (linkedRes.payload?.data?.success) {
                const linked = linkedRes.payload.data.data || [];
                setLinkedIds(linked.map(e => e.id));

                // Sync actual linked count to Redux for validation
                // Only update if value changed to prevent re-render loop
                if (selectedProject && selectedProject.embedding_count !== linked.length) {
                    dispatch(setSelectedProject({
                        ...selectedProject,
                        embedding_count: linked.length,
                    }));
                }
            }
        } catch (error) {
            console.error('Failed to fetch embeddings:', error);
            toast.error('Failed to load knowledge base');
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, projectId]); // selectedProject intentionally excluded to avoid loop

    useEffect(() => {
        if (projectId) {
            fetchData();
        }
    }, [projectId, fetchData]);

    const handleToggle = (embeddingId) => {
        setLinkedIds(prev =>
            prev.includes(embeddingId)
                ? prev.filter(id => id !== embeddingId)
                : [...prev, embeddingId]
        );
    };

    const handleSelectAll = () => {
        if (linkedIds.length === allEmbeddings.length) {
            setLinkedIds([]);
        } else {
            setLinkedIds(allEmbeddings.map(e => e.id));
        }
    };

    const handleSave = async () => {
        if (linkedIds.length === 0) {
            toast.error('Please select at least one knowledge source.');
            return;
        }

        setIsSaving(true);
        try {
            const result = await dispatch(updateProjectEmbeddings({
                project_id: projectId,
                embedding_ids: linkedIds,
            }));

            if (result.payload?.data?.success) {
                toast.success('Knowledge base selection saved');

                // Update embedding_count in Redux so main Save button validation works
                if (selectedProject) {
                    dispatch(setSelectedProject({
                        ...selectedProject,
                        embedding_count: linkedIds.length,
                    }));
                }

                onSave?.(linkedIds);
            } else {
                toast.error(result.payload?.data?.message || 'Failed to save selection');
            }
        } catch (error) {
            console.error('Save failed:', error);
            toast.error('Failed to save knowledge base selection');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="kb-selection-loading">
                <div className="spinner"></div>
                <span>Loading knowledge base...</span>
            </div>
        );
    }

    return (
        <div className="kb-selection-container">
            <div className="kb-selection-header">
                <h4>Knowledge Base Configuration</h4>
                <p>Select which documents this chatbot can access</p>
            </div>

            {allEmbeddings.length === 0 ? (
                <div className="kb-empty-state">
                    <p>No documents in your knowledge base yet.</p>
                    <span>Upload documents to enable chatbot knowledge.</span>
                </div>
            ) : (
                <>
                    <div className="kb-selection-actions">
                        <label className="select-all-checkbox">
                            <input
                                type="checkbox"
                                checked={linkedIds.length === allEmbeddings.length && allEmbeddings.length > 0}
                                onChange={handleSelectAll}
                            />
                            <span>Select All ({linkedIds.length}/{allEmbeddings.length})</span>
                        </label>
                    </div>

                    <div className="kb-selection-list">
                        {allEmbeddings.map((embedding) => (
                            <div
                                key={embedding.id}
                                className={`kb-item ${linkedIds.includes(embedding.id) ? 'selected' : ''}`}
                                onClick={() => handleToggle(embedding.id)}
                            >
                                <input
                                    type="checkbox"
                                    checked={linkedIds.includes(embedding.id)}
                                    onChange={() => handleToggle(embedding.id)}
                                />
                                <img
                                    src={fileIcons[embedding.type] || TextIcon}
                                    alt="file"
                                    className="file-icon"
                                />
                                <div className="kb-item-info">
                                    <span className="filename">{embedding.input_file_name || 'Untitled Document'}</span>
                                    <span className="meta">
                                        {embedding.length_of_content?.toLocaleString() || '0'} characters
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="kb-selection-footer">
                        <Button
                            text={isSaving ? 'Saving...' : 'Save Selection'}
                            onClick={handleSave}
                            disabled={isSaving}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
